import { useMe } from "@/api/services/admin/auth";
import { useOrganizationDetail } from "@/api/services/admin/organization";
import { TOrganizationDetailDto, TProductContractDto } from "@/api/services/admin/organization/dtos/get-organization-detail.dto";
import { useProductContractsByOrganization } from "@/api/services/admin/product-contract";
import { IconErrorOutline, IconRefresh } from "@/assets/icons";
import { EllipsisText } from "@/components/elements/EllipsisText";
import { SegmentedControl } from "@/components/elements/SegmentedControl/SegmentedControl";
import { Table, TableSorting } from "@/components/elements/Table";
import { DATE_FORMAT } from "@/constants/datetime";
import { EOrganizationStatus } from "@/constants/enum";
import { EMPTY_STR } from "@/constants/string";
import { ContractServiceBadge } from "@/features/organization/components/ContractServiceBadge";
import { CONTRACT_FILTER_KEYS, CONTRACT_FILTER_OPTIONS } from "@/features/organization/constants/organizationDetail";
import { getContractPlanLabel } from "@/features/organization/utils/getContractPlanLabel";
import { FirstLoginModal } from "@/features/session/components/FirstLoginModal";
import { useSorting } from "@/hooks/useSorting";
import { useTable } from "@/hooks/useTable";
import { AppRoutes } from "@/routes/config";
import { black, state, text } from "@/theme/colors";
import { EContractStatus, EProductCode } from "@/types/enum";
import { dayjs } from "@/utils/dayjs";
import { isNumber } from "@/utils/number";
import { sleep } from "@/utils/pieces";
import { createUrlWithParams, openNewTab } from "@/utils/url";
import { Stack, Typography } from "@mui/material";
import { MRT_ColumnDef } from "material-react-table";
import { FC, useMemo, useState } from "react";
import { useParams } from "react-router-dom";

type Props = {
  organization?: TOrganizationDetailDto;
};

export const OrganizationDetailContractList: FC<Props> = ({ organization }) => {
  const { id } = useParams();
  const { sorts } = useSorting();
  const { data, isLoading, isFetching, refetch } = useProductContractsByOrganization(id, {
    sorts: sorts.length > 0 ? sorts : undefined,
  });
  const { refetch: reFetchOrg } = useOrganizationDetail();
  const { data: me } = useMe();

  const productContracts = data?.data ?? [];

  // First-login setup modal: selecting a contract provisions the product admin account + hands off.
  // If the logged-in admin already has an account for that product (useMe.productAccounts), it's
  // already provisioned, so skip the "processing first login" modal and open the redirect right away.
  const [firstLoginContractId, setFirstLoginContractId] = useState<number | string | null>(null);

  const handleClickContractRow = (row: TProductContractDto) => {
    const productCode = row.product?.code;
    if (productCode && me?.data.productAccounts[productCode as EProductCode]) {
      openNewTab(
        createUrlWithParams(AppRoutes.mintOauthRedirect, { organizationId: id ?? row.organizationId, productCode }),
        `productContract:${row.id}`,
      );
      return;
    }
    setFirstLoginContractId(row.id);
  };

  const [contractFilter, setContractFilter] = useState(CONTRACT_FILTER_KEYS.ALL);

  const filteredItems = useMemo(() => {
    if (!contractFilter) return productContracts;
    switch (contractFilter) {
      case CONTRACT_FILTER_KEYS.DURING_CONTRACT_AND_TRIAL: {
        return productContracts.filter(
          (item) => item.isTemporary || item.status === EContractStatus.DURING_CONTRACT || item.status === EContractStatus.TRIAL,
        );
      }
      case CONTRACT_FILTER_KEYS.CANCEL: {
        return productContracts.filter(
          (item) =>
            item.isTemporary ||
            item.status === EContractStatus.TRIAL_CANCELLED ||
            item.status === EContractStatus.CANCELED_CONTRACT ||
            item.status === EContractStatus.DELETED_CONTRACT,
        );
      }
      default: {
        return productContracts;
      }
    }
  }, [productContracts, contractFilter]);

  const columns: MRT_ColumnDef<TProductContractDto>[] = useMemo(
    () => [
      {
        id: "product.code",
        header: "",
        size: 56,
        Cell: ({ row }) =>
          row.original.product && (
            <ContractServiceBadge
              products={[{ code: row.original.product.code, singleCharLabel: row.original.product.singleCharLabel, name: row.original.product.name }]}
            />
          ),
      },
      {
        accessorKey: "product.name",
        header: "プロダクト名",
        size: 186,
        Header: () => (
          <TableSorting name="productName">
            <Typography variant="body14Bold" color={text.primary}>
              プロダクト名
            </Typography>
          </TableSorting>
        ),
        Cell: ({ row }) => {
          const isActiveContract =
            row.original?.status === null ||
            row.original?.status === EContractStatus.DURING_CONTRACT ||
            row.original.status === EContractStatus.TRIAL;
          return (
            <EllipsisText
              title={row.original.product?.name ?? ""}
              variant="body14"
              maxWidth={160}
              sx={{ textDecoration: isActiveContract ? undefined : "line-through" }}
            />
          );
        },
      },
      {
        accessorKey: "plan",
        header: "システム情報",
        size: 186,
        Cell: ({ row }) => {
          if (row.original.isTemporary) {
            return (
              <Stack direction="row" alignItems="center" gap={0.5} sx={{ color: state.error_3 }}>
                <IconErrorOutline fontSize="24px" />
                <Typography variant="body14" color={black[30]}>
                  仮登録
                </Typography>
              </Stack>
            );
          }
          if (!row.original) return EMPTY_STR.TEXT;
          const { status, plan } = row.original;
          return getContractPlanLabel(status || null, plan || "");
        },
      },
      {
        id: "userCount",
        header: "有効ユーザー数",
        size: 130,
        Cell: ({ row }) => (
          <Typography variant="body14">{isNumber(row.original?.activeUserCount) ? row.original.activeUserCount : EMPTY_STR.TEXT}</Typography>
        ),
      },
      {
        id: "lastLoginAt",
        header: "最終ログイン",
        size: 148,
        Header: () => (
          <TableSorting name="lastLoginAt">
            <Typography variant="body14Bold" color={text.primary}>
              最終ログイン
            </Typography>
          </TableSorting>
        ),
        Cell: ({ row }) => {
          const date = row.original.lastLoginAt;
          return date ? dayjs(date).format(DATE_FORMAT) : EMPTY_STR.DATE;
        },
      },
      {
        id: "createdAt",
        header: "登録日",
        size: 106,
        Header: () => (
          <TableSorting name="createdAt">
            <Typography variant="body14Bold" color={text.primary}>
              登録日
            </Typography>
          </TableSorting>
        ),
        Cell: ({ row }) => {
          const date = row.original.createdAt;
          return date ? dayjs(date).format(DATE_FORMAT) : EMPTY_STR.DATE;
        },
      },
    ],
    [],
  );

  const isDeleted = organization?.status === EOrganizationStatus.DELETED;

  const { table } = useTable({
    columns,
    rows: filteredItems,
    onClickRow: isDeleted ? undefined : handleClickContractRow,
    muiTableBodyRowProps: ({ row }) => ({
      sx: {
        backgroundColor: row.original?.isTemporary ? `${state.warning_3} !important` : undefined,
      },
    }),
  });

  const handleRefetchOrg = () => {
    refetch();
    reFetchOrg();
  };

  return (
    <Stack gap={2}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="head20Bold" color={black[30]}>
          契約一覧 ({filteredItems.length})
        </Typography>
        <SegmentedControl value={contractFilter} options={CONTRACT_FILTER_OPTIONS} onChange={setContractFilter} />
      </Stack>
      <FirstLoginModal
        isOpen={firstLoginContractId != null}
        organizationId={id}
        productContractId={firstLoginContractId ?? undefined}
        onClose={() => setFirstLoginContractId(null)}
      />
      <Table table={table} isLoading={isLoading} isFetching={isFetching} />
      <Stack direction="row" justifyContent="space-between" gap={2} alignItems="center">
        <Typography variant="body14" color={text.secondary}>
          各プロダクトを選ぶと、プロダクト管理画面におけるお客様の詳細情報を開くことができます。{" "}
        </Typography>
        <Stack
          flexDirection="row"
          alignItems="center"
          onClick={isFetching ? undefined : handleRefetchOrg}
          gap={1}
          sx={{
            padding: "8px 16px",
            cursor: isFetching ? "default" : "pointer",
            opacity: isFetching ? 0.5 : 1,
            pointerEvents: isFetching ? "none" : "auto",
            ":hover": {
              opacity: isFetching ? 0.5 : 0.7,
              transition: "all 0.2s ease",
            },
          }}
        >
          <Stack
            sx={{
              display: "flex",
              mt: "2px",
              justifyContent: "center",
              alignItems: "center",
              animation: isFetching ? "spin 1s linear infinite" : "none",
              "@keyframes spin": {
                "0%": { transform: "rotate(0deg)" },
                "100%": { transform: "rotate(360deg)" },
              },
            }}
          >
            <IconRefresh />
          </Stack>
          <Typography variant="body14" color={text.secondary}>
            登録状態を更新
          </Typography>
        </Stack>
      </Stack>
    </Stack>
  );
};
